import React, { useEffect, useCallback, useRef } from "react";

import "./PrivacyPolicy.scss";
import { useSelector } from "react-redux";
import { ApplicationState } from "../../../store";
import { LayoutState } from "../../../store/reducers/layout-reducer";
import { PrivacyState } from "../../../store/reducers/privacy-reducer";
import { VersionHistoryModal } from "../../../components/VersionHistoryModal/VersionHistoryModal";
import { LanguageModal } from "../../../components/LanguageModal/LanguageModal";
import FocusTrapping from "../../../components/__hoc/FocusTrapping";
import { Container } from "../../../components/Grid/Grid";
import { IFRAME_ID } from "../../../constants/privacyPolicyConstants";
import { usePrivacyPolicyTabs } from "../../../hooks/usePrivacyPolicyTabs";
import { usePrivacyPolicyIframe } from "../../../hooks/usePrivacyPolicyIframe";
import { usePrivacyPolicyVersionHistory } from "../../../hooks/usePrivacyPolicyVersionHistory";
import { parseLanguageString } from "../../../utils/privacyPolicyUtils";
import { PrivacyPolicyHeader } from "./components/PrivacyPolicyHeader";

export const PrivacyPolicy: React.FC = () => {
  // Get layout and privacy state from Redux
  const layoutState = useSelector<ApplicationState, LayoutState>(
    (state) => state.layout
  );
  const privacyState = useSelector<ApplicationState, PrivacyState>(
    (state) => state.privacy
  );

  // Tab management hook
  const {
    activeTab,
    activeTabType,
    shouldShowTabs,
    hasLPP,
    tabItem,
    handleTabChange,
  } = usePrivacyPolicyTabs();

  // Version history management hook
  const {
    versionHistories,
    hasVersionHistory,
    selectedHistory,
    ppSource,
    handleSelectHistory,
    loadPrivacyPolicyVersionHistory,
  } = usePrivacyPolicyVersionHistory(
    privacyState.language,
    activeTabType,
    hasLPP
  );

  // Iframe management hook
  const { isIframeLoaded, iframeLoaded, triggerIframeReflow } =
    usePrivacyPolicyIframe(layoutState.isDarkMode);

  // Modal state
  const [isLanguageOpen, setLanguageOpen] = React.useState(false);
  const [isHistoryOpen, setHistoryOpen] = React.useState(false);

  // Refs
  const topRef = useRef<HTMLDivElement>(null);

  // Parse language
  const { language } = privacyState;
  const [langCode, countryCode] = parseLanguageString(language);

  // Close handlers with useCallback for performance
  const handleCloseVersionHistory = useCallback(() => {
    setHistoryOpen(false);
    document.getElementById("version-history-button")?.focus();
  }, []);

  const handleClosePolicyLanguage = useCallback(() => {
    setLanguageOpen(false);
    document.getElementById("policy-language-button")?.focus();
  }, []);

  // Open handlers with useCallback for performance
  const handleOpenVersionHistory = useCallback(() => setHistoryOpen(true), []);
  const handleOpenPolicyLanguage = useCallback(() => setLanguageOpen(true), []);

  // Handle version history selection and close modal
  const handleVersionHistorySelect = useCallback(
    async (history: Parameters<typeof handleSelectHistory>[0]) => {
      await handleSelectHistory(history);
      setHistoryOpen(false);
      document.getElementById("version-history-button")?.focus();
    },
    [handleSelectHistory]
  );

  // Load version history on language/tab change
  useEffect(() => {
    if (language === "") return;
    loadPrivacyPolicyVersionHistory(
      countryCode,
      langCode,
      shouldShowTabs ? activeTabType : undefined
    );
  }, [
    language,
    activeTabType,
    shouldShowTabs,
    countryCode,
    langCode,
    loadPrivacyPolicyVersionHistory,
  ]);
  // Initial iframe reflow trigger
  useEffect(() => {
    const iframeReflow = setTimeout(() => {
      triggerIframeReflow();
    }, 0);
    topRef.current?.focus();
    return () => {
      clearTimeout(iframeReflow);
    };
  }, [triggerIframeReflow]);

  // Focus management for modals
  useEffect(() => {
    if (isLanguageOpen) {
      document.getElementById("language-modal-tts")?.focus();
    } else if (isHistoryOpen) {
      document.getElementById("version-modal-tts")?.focus();
    }
  }, [isLanguageOpen, isHistoryOpen]);

  return (
    <div className="privacy-policy">
      {/* Header Section */}
      <div ref={topRef} tabIndex={-1}>
        {/* Version History Modal */}
        <FocusTrapping
          onHandleClose={handleCloseVersionHistory}
          isActive={isHistoryOpen}
          modalId="version-history-modal"
        >
          <VersionHistoryModal
            headertestid="onhide-history-modal"
            selectedHistory={selectedHistory}
            show={isHistoryOpen}
            onHide={handleCloseVersionHistory}
            versionHistories={versionHistories}
            onHistorySelect={handleVersionHistorySelect}
          />
        </FocusTrapping>
        <PrivacyPolicyHeader
          hasVersionHistory={hasVersionHistory}
          ppSource={ppSource}
          onVersionHistoryClick={handleOpenVersionHistory}
          onLanguageClick={handleOpenPolicyLanguage}
          shouldShowTabs={shouldShowTabs}
          tabItem={tabItem}
          activeTab={activeTab}
          onTabChange={handleTabChange}
        />
      </div>

      {/* Language Modal */}
      <FocusTrapping
        onHandleClose={handleClosePolicyLanguage}
        isActive={isLanguageOpen}
        modalId="language-modal-privacy-policy"
      >
        <LanguageModal
          headertestid="onhide-language-modal"
          id="language-modal-privacy-policy"
          key={1}
          show={isLanguageOpen}
          isSiteLanguage={false}
          onHide={handleClosePolicyLanguage}
        />
      </FocusTrapping>

      {/* Iframe Container */}
      {isIframeLoaded && (
        <Container>
          <iframe
            className={"mt-top-61"}
            id={IFRAME_ID}
            src={ppSource}
            width="100%"
            title="privacy-policy"
            onLoad={iframeLoaded}
          />
        </Container>
      )}
    </div>
  );
};
